import noop from 'lodash/noop';

import { mergeLogOptions, toLoggableError } from './errors/LoggableError';
import logError, { LogErrorOptions } from './log-error';
import { logEventString, LogEventStringOptions } from './log-event';
import { COUNTERS, STATUS } from './logger-enums';
import { configureLogger, Logger } from './loggerFacade';
import { countMetrika } from './metrika';
import isOnPremise from './onpremise/is-onpremise';

const additionalProviders: Array<() => LogErrorOptions['additional']> = [];

export const addAdditionalProviders = (provider: () => LogErrorOptions['additional']) => {
    additionalProviders.push(provider);
};

const getAdditionalFromProviders = () => {
    return additionalProviders.reduce((aux, provider) => {
        return {
            ...aux,
            ...provider(),
        };
    }, {});
};

function createOnPremLogger(block: string): Logger {
    return {
        // eslint-disable-next-line no-console
        info: (...args) => console.info(block, ...args),
        // eslint-disable-next-line no-console
        debug: (...args) => console.debug(block, ...args),
        // eslint-disable-next-line no-console
        log: (...args) => console.log(block, ...args),
        // eslint-disable-next-line no-console
        warn: (...args) => console.warn(block, ...args),
        // eslint-disable-next-line no-console
        error: (...args) => console.error(block, ...args),
        event: noop,
        featureEvent: noop,
        startDelta: noop,
        endDelta: noop,
        catch(options) {
            return (error) => {
                this.error(error, options);
            };
        },
        startFeatureDelta: noop,
        endFeatureDelta: noop,
    };
}

const timeouts: { [key: string]: TimeoutType } = {};

function createCloudLogger(block: string): Logger {
    return {
        // eslint-disable-next-line no-console
        info: (...args) => console.info(block, ...args),
        // eslint-disable-next-line no-console
        debug: (...args) => console.debug(block, ...args),
        // eslint-disable-next-line no-console
        log: (...args) => console.log(block, ...args),
        // eslint-disable-next-line no-console
        catch(options) {
            return (error: unknown) => {
                this.error(error, options);
            };
        },
        warn: (message: unknown, logFieldsOrAny: LogErrorOptions = {}) => {
            const loggableMessage = toLoggableError(message);
            const sourceFields = loggableMessage.loggerFields;
            const logFields = typeof logFieldsOrAny === 'object' ? logFieldsOrAny : undefined;

            const {
                metrikaName = undefined,
                metrikaParams = [],
                ...fields
            } = mergeLogOptions(
                sourceFields,
                {
                    block,
                    method: sourceFields?.method || 'unknown',
                    level: 'warning',
                    ...logFields,
                },
                getAdditionalFromProviders()
            );

            logError(fields, loggableMessage);

            // eslint-disable-next-line no-console
            console.warn(block, message, fields);

            if (metrikaName) {
                countMetrika(metrikaName, ...metrikaParams);
            }
        },
        error: (error: unknown, logFieldsOrAny: LogErrorOptions = {}) => {
            const loggableError = toLoggableError(error);
            const sourceFields = loggableError.loggerFields;
            const logFields = typeof logFieldsOrAny === 'object' ? logFieldsOrAny : undefined;

            const {
                metrikaName = undefined,
                metrikaParams = [],
                ...fields
            } = mergeLogOptions(
                sourceFields,
                {
                    block,
                    method: sourceFields?.method || 'unknown',
                    level: 'error',
                    ...logFields,
                },
                getAdditionalFromProviders()
            );

            logError(fields, loggableError);

            // eslint-disable-next-line no-console
            console.error(block, error, fields);

            if (metrikaName) {
                countMetrika(metrikaName, ...metrikaParams);
            }
        },
        event: (name: string, options?: LogEventStringOptions) => {
            const mergedOptions = {
                block,
                ...options,
                additional: {
                    ...getAdditionalFromProviders(),
                    ...options?.additional,
                },
            };

            logEventString(name, mergedOptions);

            // eslint-disable-next-line no-console
            console.debug(block, name, mergedOptions);
        },
        featureEvent: ({ feature, action, options, isSuccess, timeoutMS, onFail }) => {
            const mergedOptions = {
                ...options,
                additional: encodeURIComponent(
                    JSON.stringify({
                        ...getAdditionalFromProviders(),
                        ...options?.additional,
                    })
                ),
            };

            const prefix = `${feature}_${action}_event`;
            const eventName = `${prefix}${
                isSuccess !== undefined ? (isSuccess ? `_${STATUS.SUCCESS}` : `_${STATUS.FAIL}`) : ''
            }`;
            try {
                window?.Ya.Rum.sendTimeMark(`${eventName}`, undefined, undefined, mergedOptions);

                if (isSuccess !== undefined && timeouts[prefix] !== undefined) {
                    clearTimeout(timeouts[prefix]);
                    delete timeouts[prefix];
                }

                if (timeoutMS !== undefined) {
                    timeouts[prefix] = setTimeout(() => {
                        onFail?.();
                        const failByTimeoutMergedOptions = {
                            ...options,
                            additional: encodeURIComponent(
                                JSON.stringify({
                                    ...getAdditionalFromProviders(),
                                    ...options?.additional,
                                    fail_by_timeout: true,
                                })
                            ),
                        };
                        window?.Ya.Rum.sendTimeMark(
                            `${prefix}_${STATUS.FAIL}`,
                            undefined,
                            undefined,
                            failByTimeoutMergedOptions
                        );
                        delete timeouts[prefix];
                    }, timeoutMS);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        startDelta: (counterId: COUNTERS) => {
            try {
                window?.Ya.Rum.time(counterId);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        endDelta: (counterId: COUNTERS, vars?: object) => {
            try {
                window?.Ya.Rum.timeEnd(counterId, {
                    additional: encodeURIComponent(
                        JSON.stringify({
                            ...getAdditionalFromProviders(),
                            ...vars,
                        })
                    ),
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        startFeatureDelta: ({ feature, action, options, timeoutMS, onFail }) => {
            const mergedOptions = {
                ...options,
                additional: encodeURIComponent(
                    JSON.stringify({
                        ...getAdditionalFromProviders(),
                        ...options?.additional,
                        fail_by_timeout: true,
                    })
                ),
            };
            const prefix = `${feature}_${action}_delta`;
            try {
                window?.Ya.Rum.time(`${prefix}_${STATUS.SUCCESS}`);
                window?.Ya.Rum.time(`${prefix}_${STATUS.FAIL}`);
                if (timeoutMS !== undefined) {
                    timeouts[prefix] = setTimeout(() => {
                        onFail?.();
                        window?.Ya.Rum.timeEnd(`${prefix}_${STATUS.FAIL}`, mergedOptions);
                        delete timeouts[prefix];
                    }, timeoutMS);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        endFeatureDelta: ({ feature, action, isSuccess, options }) => {
            const prefix = `${feature}_${action}_delta`;
            const mergedOptions = {
                ...options,
                additional: encodeURIComponent(
                    JSON.stringify({
                        ...getAdditionalFromProviders(),
                        ...options?.additional,
                    })
                ),
            };
            try {
                window?.Ya.Rum.timeEnd(`${prefix}_${isSuccess ? STATUS.SUCCESS : STATUS.FAIL}`, mergedOptions);
                clearTimeout(timeouts[prefix]);
                delete timeouts[prefix];
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
    };
}

configureLogger(isOnPremise ? createOnPremLogger : createCloudLogger);
